<template>
  <ExpandableCard
    :expanded="isExpanded"
    @minimised="minimise"
    @maximised="maximise"
    @toggled="toggle"
  >
    <template v-slot:header>
      <span data-test="party-name">{{ partyName }}</span>
      <span
        v-show="isExpanded"
        class="text-midGray text-12">
        <span class="px-sm">|</span>
        <button
          @click="addMyselfAsParty"
          class="naked text-midGray cursor-pointer underline hover:text-black"
          data-test="add-myself"
        >
          {{ $t('ui.envelopes.cards.partyRoleCard.addMyselfAsParty') }}
        </button>
      </span>
    </template>

    <template v-slot:controls>
      <div class="flex gap-sm items-center">
        <ToggleControl
          :value="isOnlyCopy"
          :label="$t('ui.envelopes.cards.partyRoleCard.receiveOnlyCopy')"
          @toggled="receiveOnlyACopy"
          data-test="only-copy"
        />
        <MinimiseControl
          :disabled="!isExpanded"
          @minimised="minimise"
          data-test="minimise"
        />
        <MaximiseControl
          :disabled="isExpanded"
          @maximised="maximise"
          data-test="maximise"
        />
        <XMarkControl
          @click.native="remove"
          data-test="remove"
        />
      </div>
    </template>

    <template
      v-if="!isExpanded"
      v-slot:content
    >
      <div>
        <slot name="overview" />
      </div>
    </template>

    <template
      v-else
      v-slot:expandedContent
    >
      <div>
        <slot name="form" />
      </div>
      <div>
        <slot name="templateSelector" />
      </div>
    </template>
  </ExpandableCard>
</template>

<script>
import { defineComponent, ref, watchEffect } from 'vue';
import { ToggleControl, MaximiseControl, MinimiseControl, XMarkControl } from '@/components/ui/common/cardControls';
import { ExpandableCard } from '@/components/library/cards';
import { createEventLogger } from '@/utils/analytics';

export default defineComponent({
  name: 'PartyRoleCard',
  components: {
    ExpandableCard,
    ToggleControl,
    MaximiseControl,
    MinimiseControl,
    XMarkControl,
  },
  props: {
    partyName: {
      type: String,
      required: true,
    },
    onlyCopy: {
      type: Boolean,
      required: true,
    },
    expanded: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * This is the equivalent of data-ec. If it's empty,
     * nothing will be logged in this component. Event types
     * (the equivalent of data-et) are hard-coded in this
     * component.
     */
    logEventCategory: {
      type: String,
      required: false,
    },
  },
  emits: [
    'addedMyselfAsParty',
    'receivedOnlyACopy',
    'removed',
  ],
  setup(props, { emit }) {
    const isExpanded = ref(null);
    const isOnlyCopy = ref(null);

    const logEvent = createEventLogger(props.logEventCategory);

    watchEffect(() => {
      isExpanded.value = props.expanded;
      isOnlyCopy.value = props.onlyCopy;
    });

    function minimise() {
      isExpanded.value = false;
      emit('minimised');
      logEvent('minimised_card');
    }

    function maximise() {
      isExpanded.value = true;
      emit('maximised');
      logEvent('maximised_card');
    }

    function toggle() {
      isExpanded.value = !isExpanded.value;
      emit('toggled', isExpanded.value);
      logEvent('toggled_card');
    }

    function addMyselfAsParty() {
      emit('addedMyselfAsParty');
      logEvent('added_myself_as_party');
    }

    function receiveOnlyACopy() {
      emit('receivedOnlyACopy', !isOnlyCopy.value);
      logEvent('receive_only_a_copy', {
        toggledOn: !isOnlyCopy.value,
      });
    }

    function remove() {
      emit('removed');
      logEvent('removed_party');
    }

    return {
      isExpanded,
      isOnlyCopy,
      minimise,
      maximise,
      toggle,
      addMyselfAsParty,
      receiveOnlyACopy,
      remove,
    };
  },
});
</script>
